/**
 * Floating feedback widget — accessible from all pages.
 * Sends general site feedback to POST /api/v1/feedback.
 */

import React, { useState } from 'react';
import { MessageSquare, X } from 'lucide-react';
import { API_CONFIG } from '../../config/api';

interface FeedbackWidgetProps {
  currentPage?: string;
}

const FeedbackWidget: React.FC<FeedbackWidgetProps> = ({ currentPage }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;

    setStatus('sending');
    try {
      const base = API_CONFIG.baseUrl || window.location.origin;
      const res = await fetch(`${base}${API_CONFIG.endpoints.feedback}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: message.trim(),
          email: email.trim() || null,
          page: currentPage || null,
        }),
      });
      if (!res.ok) throw new Error('Failed to submit');
      setStatus('success');
      setMessage('');
      setEmail('');
      setTimeout(() => {
        setIsOpen(false);
        setStatus('idle');
      }, 2000);
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="no-print fixed bottom-6 right-6 z-50">
      {/* Popover form */}
      {isOpen && (
        <div className="mb-3 w-80 rounded-lg border border-border bg-background shadow-xl">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <h3 className="text-sm font-semibold text-foreground">Send Feedback</h3>
            <button
              onClick={() => { setIsOpen(false); setStatus('idle'); }}
              className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
              aria-label="Close feedback form"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {status === 'success' ? (
            <div className="p-4 text-center text-sm text-emerald-600 dark:text-emerald-400">
              Thank you for your feedback!
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-4 space-y-3">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="What's on your mind?"
                required
                rows={3}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-amber-500/50 resize-none"
              />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email (optional)"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
              {status === 'error' && (
                <p className="text-xs text-red-500">Something went wrong. Please try again.</p>
              )}
              <button
                type="submit"
                disabled={status === 'sending' || !message.trim()}
                className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                {status === 'sending' ? 'Sending...' : 'Submit'}
              </button>
            </form>
          )}
        </div>
      )}

      {/* Floating trigger button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        aria-label="Send feedback"
      >
        <MessageSquare className="h-5 w-5" />
      </button>
    </div>
  );
};

export default FeedbackWidget;
